import dayjs from 'dayjs';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { Props } from 'recharts/types/component/ResponsiveContainer';

import { EmptyData } from '@atom/empty-data/empty-data';
import { Text } from '@atom/typography/typography';
import { WrapperTooltip } from '@component/chart/chart.style';
import { DateFormatEnum } from '@enum/date-format.enum';
import { ColorEnum } from '@enum/style/color.enum';
import { useLoadApplicationChartStatsQuery } from '@hook/stats/stats.hooks';
import { isString } from '@util/data/is-exist';

interface ChartProps extends Omit<Props, 'children'> {
  applicationId: string;
}

const CustomTooltip = ({ active, payload }: any) => {
  if (!active) return null;

  return (
    <WrapperTooltip>
      <Text color={ColorEnum.Black10} level='l' paragraph>
        {dayjs(payload[0].payload.date).format(DateFormatEnum.Day)}
      </Text>
      {(payload as any[]).map((item, index) => (
        <Text key={index} color={ColorEnum.Black10} level='l' paragraph>
          {item.name}:{' '}
          <Text color={item.color} level='l'>
            {item.value}
          </Text>
        </Text>
      ))}
    </WrapperTooltip>
  );
};

export const Chart = ({ applicationId, ...props }: ChartProps) => {
  const { data: dataChart } = useLoadApplicationChartStatsQuery(applicationId, {
    skip: !isString(applicationId),
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const chartData = (dataChart?.data ?? []).map((item) => ({
    ...item,
    name: dayjs(item.date).format('DD MMM'),
  }));

  if (chartData.length === 0) return <EmptyData title='Not data available yet' />;

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <ResponsiveContainer {...props}>
      <AreaChart data={chartData}>
        <defs>
          <linearGradient id='colorU1' x1='0' x2='0' y1='0' y2='1'>
            <stop offset='5%' stopColor='#9ECE87' stopOpacity={0.9} />
            <stop offset='100%' stopColor='#9ECE87' stopOpacity={0.1} />
          </linearGradient>
          <linearGradient id='colorU2' x1='0' x2='0' y1='0' y2='1'>
            <stop offset='5%' stopColor='#E38691' stopOpacity={0.9} />
            <stop offset='100%' stopColor='#E38691' stopOpacity={0.1} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke={ColorEnum.Black70} strokeDasharray='3 0' strokeOpacity={0.2} vertical={false} />
        <XAxis dataKey='name' stroke={ColorEnum.Black70} strokeOpacity={0.2} />
        <YAxis stroke={ColorEnum.Black70} strokeOpacity={0} />
        <Tooltip content={<CustomTooltip />} />
        <Area dataKey='count' fill='url(#colorU1)' name='Requests' stroke='#9ECE87' strokeWidth={2} type='monotone' />
      </AreaChart>
    </ResponsiveContainer>
  );
};
